import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form941XDueDate = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 941-X Due Dates | Important Deadlines for Corrections"
          description="Stay informed about the due dates for Form 941-X to correct errors on previously filed 941 forms. Ensure timely submission to avoid penalties."
          keywords="Form 941, Payroll tax filing, IRS Form 941, Employment tax, Quarterly tax reporting"
          canonical="resources/employment-tax/form-941-x-due-dates/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 941-X </span>
          </span>{' '}
          Due Dates
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-x-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941-X Due Dates at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <b>
                          Form 941-X, Adjusted Employer’s Quarterly Federal Tax
                          Return or Claim for Refund
                        </b>
                        , is used by employers to correct errors on a previously
                        filed Form 941. Employers can use it to correct
                        underreported or overreported employment taxes,
                        including federal income tax withholding and Social
                        Security and Medicare taxes.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>When to file:</b>
                        {
                          ' File Form 941-X as soon as you discover an error on a previously filed Form 941. '
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>Underreported tax:</b>
                        {
                          ' File Form 941-X by the due date of the Form 941 for the quarter in which you discover the error and pay the amount owed. '
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <b>Overreported tax:</b>
                        {' You can '}
                        generally file
                        {' Form 941-X within '}
                        <b>{'3 years '}</b>
                        {'from the date Form 941 was filed '}
                        or
                        <b>{' 2 years '}</b>
                        from the date the tax was paid, whichever is later
                        {'. '}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <b>Penalties and interest:</b>
                        {
                          ' Timely filing and payment of underreported tax may help avoid certain penalties and interest'
                        }
                        .
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <Link
                        href="/resources/employment-tax/form-941-x-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form 941-X
                      </Link>
                      , officially known as the{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        &quot;Adjusted Employer&apos;s Quarterly Federal Tax
                        Return or Claim for Refund,&quot;
                      </span>{' '}
                      is a critical document for employers to correct errors on
                      previously filed Form 941.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      Employers use{' '}
                      <Link
                        href="/resources/employment-tax/form-941-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 941
                      </Link>{' '}
                      to report income taxes, Social Security tax, or Medicare
                      tax withheld from employees&apos; paychecks, as well as
                      the employers portion of Social Security or Medicare tax.
                      This guide will help you understand when and how to file
                      Form 941-X to ensure compliance and avoid penalties.
                    </Typography>
                  </Box>
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When Should You File Form 941-X?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#isthere" sx={{ color: '#0202ff' }}>
                          Is There a Deadline for Filing Form 941-X?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where Should You File Form 941-X?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What About Penalties and Interest?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="when"
                >
                  When Should You File Form 941-X?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  <Link
                    href="/employment-tax/efile-irs-form-941-x-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    File Form 941-X
                  </Link>{' '}
                  as soon as you discover an error on a previously filed Form
                  941. However, do not file Form 941-X if your only errors
                  relate to:
                </Typography>

                <ul>
                  <li style={{ marginBottom: 12 }}>
                    The number of employees who received wages (Form 941, Line
                    1)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Federal tax liabilities reported on Form 941, Part 2 or on{' '}
                    <Link
                      href="/resources/employment-tax/form-941-schedule-b-filing-requirements/"
                      sx={{ color: '#0202ff' }}
                    >
                      Schedule B (Form 941)
                    </Link>{' '}
                    Report of Tax Liability for Semiweekly Schedule Depositors
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Due Dates for Filing Form 941-X:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>Underreported Tax:</b> File Form 941-X by the due date of
                    the return for the period in which you discovered the error
                    and pay the amount owed to avoid penalties and interest.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Overreported Tax:</b>
                    <ol>
                      <li style={{ marginBottom: 12 }}>
                        <b>Adjustment Process:</b> File Form 941-X soon after
                        discovering the error but more than 90 days before the
                        period of limitations expires.
                      </li>
                      <li style={{ marginBottom: 12 }}>
                        <b> Claim Process:</b> File Form 941-X any time before
                        the period of limitations on credit or refund expires.
                      </li>
                    </ol>
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Deadlines for Underreported Tax:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            If you discover an error in . . .
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Form 941-X is due . . .
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>January, February, March</TableCell>

                          <TableCell>April 30</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>April, May, June</TableCell>

                          <TableCell>July 31</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>July, August, September</TableCell>

                          <TableCell>October 31</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>October, November, December</TableCell>

                          <TableCell>January 31</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If filing late, attach an amended Schedule B to Form 941-X to
                  avoid an “averaged” FTD penalty.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If any due date falls on a Saturday, Sunday, or
                  legal holiday, you may file Form 941-X on the next business
                  day.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="isthere"
                >
                  Is There a Deadline for Filing Form 941-X?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can correct overreported taxes on a previously filed Form
                  941 by filing Form 941-X within 3 years of the date Form 941
                  was filed or 2 years from the date you paid the tax, whichever
                  is later. These periods are called limitations.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Example:</b> If you filed your 2023 fourth quarter Form
                  941 on January 25, 2024, the IRS considers it filed on April
                  15, 2024. If you discover an overreported amount on January
                  29, 2027, you must file Form 941-X by April 15, 2027.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you file Form 941-X to correct overreported taxes in the
                  last 90 days of the period of limitations, use the claim
                  process and not the adjustment process. For underreported
                  taxes, file a separate Form 941-X and pay any tax due.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="where"
                >
                  Where Should You File Form 941-X?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You are encouraged to file{' '}
                  <Link
                    href="/employment-tax/efile-irs-form-941-x-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 941-X
                  </Link>{' '}
                  electronically. When mailing a paper <br />
                  Form 941-X , use the following addresses based on your
                  location:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Mailing Addresses for Form 941-X:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', textAlign: 'left' }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'left',
                              fontWeight: 600,
                            }}
                          >
                            If you’re in . . .
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'left',
                              fontWeight: 600,
                            }}
                          >
                            THEN use this address . . .
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow
                          sx={{ textAlign: 'left', justifyContent: 'left' }}
                        >
                          <TableCell sx={{ textAlign: 'left' }}>
                            Connecticut, Delaware, District of Columbia,
                            Florida, Georgia, Illinois, Indiana, Kentucky,
                            Maine, Maryland, Massachusetts, Michigan, New
                            Hampshire, New Jersey, New York, North Carolina,
                            Ohio, Pennsylvania, Rhode Island, South Carolina,
                            Tennessee, Vermont, Virginia, West Virginia,
                            Wisconsin
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Department of the Treasury
                            <br /> Internal Revenue Service
                            <br /> Cincinnati, <br />
                            OH 45999-0005
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Alabama, Alaska, Arizona, Arkansas, California,
                            Colorado, Hawaii, Idaho, Iowa, Kansas, Louisiana,
                            Minnesota, Mississippi, Missouri, Montana, Nebraska,
                            Nevada, New Mexico, North Dakota, Oklahoma, Oregon,
                            South Dakota, Texas, Utah, Washington, Wyoming
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Department of the Treasury
                            <br /> Internal Revenue Service <br />
                            Ogden, UT 84201-0005
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ textAlign: 'left' }}>
                            No legal residence or principal place of business in
                            any state
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Internal Revenue Service
                            <br /> P.O. Box 409101 Ogden,
                            <br /> UT 84409
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ textAlign: 'left' }}>
                            <b>
                              Special filing address for exempt organizations
                            </b>
                            ; federal, state, and local governmental entities;
                            and Indian tribal governmental entities, regardless
                            of location
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Department of the Treasury
                            <br /> Internal Revenue Service <br />
                            Ogden, UT 84201-0005
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  PDSs (Private Delivery Services) cannot deliver to P.O. boxes.
                  Use the U.S. Postal Service to mail an item to a P.O. box
                  address. For a current list of PDSs, visit{' '}
                  <Link
                    href="https://www.irs.gov/filing/private-delivery-services-pds"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS PDS site
                  </Link>
                  . If you{' '}
                  <Link
                    href="/employment-tax/efile-irs-form-941-x-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 941-X
                  </Link>{' '}
                  using a PDS, send it to the following address.
                </Typography>
                <Typography variant="body1" id="what" sx={{ mb: 4 }}>
                  <b>Ogden - Internal Revenue Submission Processing Center </b>
                  <br />
                  1973 Rulon White Blvd. <br />
                  Ogden, UT 84201
                </Typography>

                {/* <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Employers with seasonal workers: Employers with seasonal
                      or intermittent workers may not be required to file Form
                      941.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Household employers: If you&apos;re a household employer
                      paying employees with no tax liability, you may not need
                      to file Form 941.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Agricultural employers: Employers in the farming industry
                      are not required to file Form 941. They will need to file
                      a different form, Form 943: Employer&apos;s Annual Federal
                      Tax Return for Agricultural Employees.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Employers who qualify for annual filing: Some small
                      employers with low tax liabilities may qualify for annual
                      filing rather than quarterly filing. In this case, you
                      will need to file Form 944 instead of Form 941.
                    </ListItemText>
                  </ListItem>
                </List> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 3,
                    scrollMarginTop: 87,
                  }}
                  id="inform"
                >
                  What About Penalties and Interest?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Your correction of an underreported tax amount generally won’t
                  be subject to an FTP penalty, FTD penalty, or interest if you:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    File on time (by the{' '}
                    <Link
                      href="/resources/employment-tax/form-941-filing-deadlines-and-penalties-guide/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      due date of Form 941
                    </Link>{' '}
                    for the quarter in which you discover the error)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Pay the amount shown on line 27 by the time you file Form
                    941-X
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the date you discovered the error
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Explain in detail the grounds and facts for the correction
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, no correction will be eligible for interest-free
                  treatment if:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    The amounts underreported relate to an issue raised in a
                    prior period examination
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    You knowingly underreported your employment tax liability
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    You received a notice and demand for payment
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    You received a notice of determination under section 7436
                  </li>
                </ul>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you receive a penalty notice after filing, reply with an
                  explanation. The IRS will determine if you meet the reasonable
                  cause criteria. Do not attach an explanation when you file
                  your return.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 940 within a few minutes using TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Choose a simplified tax filing process. Choose TaxZerone.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=940duedates`}
              >
                E-file Form 940
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container> */}
    </AppLayout>
  );
};

export default Form941XDueDate;
